import axios from "axios";
import AuthService from "./authentication.service";
import {
  getFreshAccessToken,
  initTokenRefresh,
  isAccessTokenExpired,
  isAuthEndpoint,
  refreshAccessToken,
} from "./tokenRefresh";
import { SubscriptionModal } from "../app/components/SubscriptionModal/SubscriptionModal";
import {
  NETWORK_MESSAGES,
  backendMessage,
  isCanceledError,
  isOffline,
  isTimeoutError,
} from "./requestErrors";
import { showConnectionNotice, watchConnection } from "./connectionNotice";

const { REACT_APP_API_BASE_URL } = process.env;
const isGuestShare = process.env.REACT_APP_API_IS_GUEST;
const { REACT_APP_FILEMANAGER_API_BASE_URL } = process.env;

export const httpFile = axios.create({
  baseURL: REACT_APP_FILEMANAGER_API_BASE_URL,
  timeout: 60000,
  headers: { "Content-Type": "application/json" },
});


const https = axios.create({
  baseURL: REACT_APP_API_BASE_URL,
  timeout: 60000,
  headers: { "Content-Type": "application/json" },
});

let subscriptionModalOpen = false;

const bearerOf = (config) => {
  const header =
    config?.headers?.get?.("Authorization") ?? config?.headers?.Authorization;
  return typeof header === "string" ? header.replace(/^Bearer\s+/i, "") : null;
};

export const setupInterceptor = () => {
  if (!isGuestShare) initTokenRefresh();
  watchConnection();

  https.interceptors.request.use(async (config) => {
    let token = AuthService.getAccessToken();
    // The proactive timer can be throttled in a background tab, so check
    // again here rather than sending a request that is sure to 401.
    if (!isGuestShare && !isAuthEndpoint(config.url)) {
      token = await getFreshAccessToken();
      // Still expired with a refresh token on hand: the refresh just failed
      // for network reasons. Sending this request would only earn a 401 and
      // a second refresh over the same bad link, so fail it right away.
      if (
        token &&
        isAccessTokenExpired(token) &&
        AuthService.getRefreshToken()
      ) {
        const renewFailed = new Error("Session renewal failed");
        renewFailed.sessionRenewFailed = true;
        renewFailed.code = "ERR_NETWORK";
        throw renewFailed;
      }
    }
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  https.interceptors.response.use(
    (response) => response,
    async (error) => {
      // No response at all (offline, timeout, server unreachable): replace
      // axios' "Network Error" with something a user can act on. Pages read
      // error.message, so this reaches every screen.
      if (!error?.response && !isCanceledError(error)) {
        const friendly = friendlyNetworkError(error);
        showConnectionNotice(
          friendly.offline
            ? "offline"
            : error?.sessionRenewFailed
            ? "session"
            : friendly.timedOut
            ? "timeout"
            : "unreachable"
        );
        return Promise.reject(friendly);
      }

      const config = error?.config;
      if (
        error?.response?.status === 401 &&
        !isGuestShare &&
        !isAuthEndpoint(config?.url)
      ) {
        if (config && !config._authRetried) {
          config._authRetried = true;
          try {
            // Concurrent 401s share one refresh; the retry goes back through
            // the request interceptor, which attaches the new token.
            await refreshAccessToken(bearerOf(config));
            return https(config);
          } catch (refreshError) {
            if (!refreshError?.sessionEnded) {
              // The token expired and the connection is too poor to renew
              // it. The session is still valid, so do not log out; tell the
              // page (and the user) it is a connection problem, not an empty
              // result. The refresh is retried with backoff.
              showConnectionNotice(isOffline() ? "offline" : "session");
              return Promise.reject(friendlyNetworkError(refreshError));
            }
          }
        }
        AuthService.endSession();
        return Promise.reject(error.response?.data || error);
      }

      if (error?.response?.status === 400) {
        handleException(error?.response?.data);
      }

      // A proxy's HTML error page (or an empty body) on a 5xx has no message
      // for pages to show; give them one.
      const status = error?.response?.status;
      const data = error?.response?.data;
      if (
        status >= 500 &&
        (data === undefined || data === null || typeof data === "string") &&
        !backendMessage(data)
      ) {
        return Promise.reject({
          message: `The server is having a problem right now (error ${status}). Please try again in a few minutes.`,
          status,
        });
      }

      return Promise.reject(error.response?.data || error);
    }
  );
};

const friendlyNetworkError = (error) => {
  const offline = isOffline();
  const timedOut = isTimeoutError(error);
  const friendly = new Error(
    offline
      ? NETWORK_MESSAGES.offline
      : error?.sessionRenewFailed || error?.refreshNetworkError
      ? NETWORK_MESSAGES.session
      : timedOut
      ? NETWORK_MESSAGES.timeout
      : NETWORK_MESSAGES.unreachable
  );
  friendly.code = error?.code;
  friendly.isNetworkError = true;
  friendly.offline = offline;
  friendly.timedOut = timedOut;
  friendly.cause = error;
  return friendly;
};

export const handleException = (data) => {
  if (data?.Errors && !subscriptionModalOpen) {
    subscriptionModalOpen = true;

    SubscriptionModal({
      onClose: () => {
        subscriptionModalOpen = false;
      },
    });
  }
};
export default https;
